import { AbsenceApprovalInboxItem } from "@/api/AbsenceApprovalInboxItem";
import { ApprovalInboxResponse } from "@/api/ApprovalInboxResponse";
import { TimesheetApprovalInboxItem } from "@/api/TimesheetApprovalInboxItem";
import ApprovalRejectDialog from "@/components/ApprovalRejectDialog";
import Content from "@/components/Content";
import StatCard from "@/components/StatCard";
import WaitingSpinner from "@/components/WaitingSpinner";
import { Button } from "@/components/ui/button";
import { Alert } from "@/components/ui/alert";
import { ApprovalStatusUtil } from "@/data/ApprovalStatusUtil";
import { formatDay, formatMinutes, formatPicker } from "@/data/DateTimeUtil";
import { absenceService } from "@/service/AbsenceService";
import { timesheetService } from "@/service/TimesheetService";
import { useProjectNames } from "@/hooks/useProjectNames";
import {
  Badge,
  Box,
  Flex,
  Group,
  Heading,
  Separator,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { ReactNode, useEffect, useRef, useState } from "react";
import {
  LuCalendarDays,
  LuCalendarClock,
  LuCheck,
  LuClipboardCheck,
  LuClock,
  LuExternalLink,
  LuEye,
  LuInbox,
  LuPencil,
  LuRefreshCw,
  LuTriangleAlert,
  LuX,
} from "react-icons/lu";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import AbsenceDialog from "../timesheet/AbsenceDialog";
import { absenceData } from "../timesheet/AbsenceData";
import TimesheetDialog from "../timesheet/TimesheetDialog";
import { timesheetData } from "../timesheet/TimesheetData";
import { useApprovalInbox } from "./ApprovalInboxContext";

type InboxTarget =
  | { type: "timesheet"; item: TimesheetApprovalInboxItem }
  | { type: "absence"; item: AbsenceApprovalInboxItem };

const employeeName = (item: TimesheetApprovalInboxItem | AbsenceApprovalInboxItem): string =>
  `${item.user.firstName} ${item.user.lastName}`;

const formatDateRange = (start: string, end: string): string =>
  start === end ? formatPicker(start) : `${formatPicker(start)} - ${formatPicker(end)}`;

const removeTimesheetItem = (
  inbox: ApprovalInboxResponse,
  collection: "pendingTimesheets" | "rejectedTimesheets",
  id: number,
): ApprovalInboxResponse => ({
  ...inbox,
  [collection]: inbox[collection].filter(item => item.entry.id !== id),
});

const removeAbsenceItem = (
  inbox: ApprovalInboxResponse,
  collection: "pendingAbsences" | "rejectedAbsences",
  id: number,
): ApprovalInboxResponse => ({
  ...inbox,
  [collection]: inbox[collection].filter(item => item.entry.id !== id),
});

const RowShell = ({
  children,
  testId,
  accentColor = "blue.500",
}: {
  children: ReactNode;
  testId: string;
  accentColor?: string;
}) => (
  <Box
    data-testid={testId}
    borderWidth="1px"
    borderColor="border"
    borderLeftWidth="4px"
    borderLeftColor={accentColor}
    rounded="lg"
    px={{ base: 3, md: 4 }}
    py={{ base: 3, md: 3 }}
    bg="bg.panel"
  >
    {children}
  </Box>
);

const EmptyState = ({ text }: { text: string }) => (
  <Stack
    align="center"
    borderWidth="1px"
    borderColor="border"
    borderStyle="dashed"
    rounded="xl"
    bg="bg.subtle"
    gap={3}
    px={6}
    py={10}
    textAlign="center"
  >
    <Flex align="center" justify="center" boxSize="3rem" rounded="full" bg="green.50" color="green.600">
      <LuInbox size="1.4rem" />
    </Flex>
    <Text color="fg.muted" fontWeight="medium">{text}</Text>
  </Stack>
);

const OverviewApprovals = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const initialTab = requestedTab === 'absences' || requestedTab === 'actionRequired' ? requestedTab : 'timesheets';
  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const { inbox, summary, isLoading, error, refresh, setInbox } = useApprovalInbox();
  const { options: projectNames } = useProjectNames();
  const [rejectTarget, setRejectTarget] = useState<InboxTarget | null>(null);
  const [dialogTarget, setDialogTarget] = useState<InboxTarget | null>(null);
  const [isDeciding, setIsDeciding] = useState(false);
  const [decisionError, setDecisionError] = useState<string | null>(null);
  const decisionInFlight = useRef(false);

  useEffect(() => { setActiveTab(initialTab); }, [initialTab]);

  const decide = async (target: InboxTarget, reason?: string): Promise<void> => {
    if (decisionInFlight.current) return;
    decisionInFlight.current = true;
    setIsDeciding(true);
    setDecisionError(null);
    try {
      const { userId, id } = target.item.entry;
      if (target.type === "timesheet") {
        if (reason === undefined) await timesheetService.approve(userId, id);
        else await timesheetService.reject(userId, id, reason);
        setInbox(current => removeTimesheetItem(current, "pendingTimesheets", id));
      } else {
        if (reason === undefined) await absenceService.approveAbsence(userId, id);
        else await absenceService.rejectAbsence(userId, id, reason);
        setInbox(current => removeAbsenceItem(current, "pendingAbsences", id));
      }
      setDialogTarget(null);
      setRejectTarget(null);
    } catch (error) {
      const conflict = (error as { status?: number } | null)?.status === 409;
      setDecisionError(t(conflict ? "approvals.decisionConflict" : "approvals.decisionFailed"));
      if (conflict) {
        await refresh();
        setDialogTarget(null);
        setRejectTarget(null);
      }
      throw error;
    } finally {
      decisionInFlight.current = false;
      setIsDeciding(false);
    }
  };

  // Decision errors are displayed here; rejection dialogs also await and handle failures.
  const approveItem = (target: InboxTarget): Promise<void> => decide(target).catch(() => undefined);

  const rejectItem = async (reason: string): Promise<void> => {
    if (!rejectTarget) return;

    await decide(rejectTarget, reason);
  };

  const resubmitTimesheet = async (item: TimesheetApprovalInboxItem["entry"]): Promise<void> => {
    await timesheetService.resubmit(item);
    setInbox(current => removeTimesheetItem(current, "rejectedTimesheets", item.id));
    setDialogTarget(null);
  };

  const cancelTimesheet = async (item: TimesheetApprovalInboxItem["entry"]): Promise<void> => {
    await timesheetService.remove(item.userId, item.id);
    setInbox(current => removeTimesheetItem(current, "rejectedTimesheets", item.id));
    setDialogTarget(null);
  };

  const resubmitAbsence = async (item: AbsenceApprovalInboxItem["entry"]): Promise<void> => {
    await absenceService.resubmitAbsence(item);
    setInbox(current => removeAbsenceItem(current, "rejectedAbsences", item.id));
    setDialogTarget(null);
  };

  const cancelAbsence = async (item: AbsenceApprovalInboxItem["entry"]): Promise<void> => {
    await absenceService.deleteAbsence(item.userId, item.id);
    setInbox(current => removeAbsenceItem(current, "rejectedAbsences", item.id));
    setDialogTarget(null);
  };

  const rejectHeading = rejectTarget?.type === "timesheet"
    ? `${employeeName(rejectTarget.item)} ${formatPicker(rejectTarget.item.entry.localDate)}`
    : rejectTarget
      ? `${employeeName(rejectTarget.item)} ${formatDateRange(rejectTarget.item.entry.localStartDate, rejectTarget.item.entry.localEndDate)}`
      : "";

  const renderTimesheetRow = (item: TimesheetApprovalInboxItem) => {
    const entryLabel = item.entry.entryId
      ? timesheetData.find(entry => entry.id === item.entry.entryId)?.name
      : undefined;
    const projectLabel = item.entry.projectId
      ? projectNames.find(project => project.value === item.entry.projectId)?.label
      : undefined;

    return (
      <RowShell key={item.entry.id} testId={`pending-timesheet-${item.entry.id}`} accentColor="blue.500">
        <Flex direction={{ base: "column", lg: "row" }} align={{ lg: "center" }} justify="space-between" gap={3}>
          <Stack gap={1.5} flex="1" minW={0}>
            <Flex align="center" gap={{ base: 1.5, md: 2.5 }} wrap="wrap" color="fg" fontSize="sm">
              <Text fontWeight="bold" fontSize="md">{employeeName(item)}</Text>
              <Badge colorPalette={ApprovalStatusUtil.color(item.entry.status)} variant="subtle" rounded="full" px={2}>
                {ApprovalStatusUtil.label(item.entry.status)}
              </Badge>
              <Badge variant="outline" colorPalette="blue" rounded="md">{formatPicker(item.entry.localDate)}</Badge>
              <Text>{item.entry.startTime}-{item.entry.endTime}</Text>
              <Text fontWeight="semibold">{formatMinutes(item.entry.minutes)} h</Text>
              {entryLabel && (
                <Flex align="center" gap={2} color="fg.muted">
                  <Text>·</Text><Text>{entryLabel}</Text>
                </Flex>
              )}
              {projectLabel && (
                <Flex align="center" gap={2} color="fg.muted">
                  <Text>·</Text><Text>{projectLabel}</Text>
                </Flex>
              )}
            </Flex>
            {item.entry.comments && (
              <Text color="fg.muted" fontSize="sm" lineClamp={1}>{item.entry.comments}</Text>
            )}
          </Stack>
          <Group gap={2} wrap="wrap" justifyContent="flex-end" alignSelf={{ base: "stretch", lg: "center" }}>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => navigate(`/admin/users/direct-reports/${item.user.id}?tab=timesheets&month=${item.entry.localDate.slice(0, 7)}`)}
            >
              <LuExternalLink /> {t("approvals.actions.viewContext")}
            </Button>
            <Button size="sm" variant="outline" disabled={isDeciding} onClick={() => {
              setDecisionError(null);
              setDialogTarget({ type: "timesheet", item });
            }}>
              <LuEye /> {t("approvals.actions.details")}
            </Button>
            <Button size="sm" colorPalette="orange" variant="outline" disabled={isDeciding} onClick={() => {
              setDecisionError(null);
              setRejectTarget({ type: "timesheet", item });
            }}>
              <LuX /> {t("approvals.actions.reject")}
            </Button>
            <Button
              size="sm"
              colorPalette="green"
              disabled={isDeciding}
              onClick={() => approveItem({ type: "timesheet", item })}
            >
              <LuCheck /> {t("approvals.actions.approve")}
            </Button>
          </Group>
        </Flex>
      </RowShell>
    );
  };

  const renderAbsenceRow = (item: AbsenceApprovalInboxItem) => {
    const absenceLabel = absenceData.find(entry => entry.type === item.entry.absenceType)?.name;

    return (
      <RowShell key={item.entry.id} testId={`pending-absence-${item.entry.id}`} accentColor="teal.500">
        <Flex direction={{ base: "column", lg: "row" }} align={{ lg: "center" }} justify="space-between" gap={3}>
          <Stack gap={1.5} flex="1" minW={0}>
            <Flex align="center" gap={{ base: 1.5, md: 2.5 }} wrap="wrap" color="fg" fontSize="sm">
              <Text fontWeight="bold" fontSize="md">{employeeName(item)}</Text>
              <Badge colorPalette={ApprovalStatusUtil.color(item.entry.status)} variant="subtle" rounded="full" px={2}>
                {ApprovalStatusUtil.label(item.entry.status)}
              </Badge>
              <Badge variant="outline" colorPalette="teal" rounded="md">
                {formatDateRange(item.entry.localStartDate, item.entry.localEndDate)}
              </Badge>
              <Text fontWeight="semibold">{formatDay(item.entry.days)} Tg</Text>
              {absenceLabel && (
                <Flex align="center" gap={2} color="fg.muted">
                  <Text>·</Text><Text>{absenceLabel}</Text>
                </Flex>
              )}
            </Flex>
            {item.entry.comments && (
              <Text color="fg.muted" fontSize="sm" lineClamp={1}>{item.entry.comments}</Text>
            )}
          </Stack>
          <Group gap={2} wrap="wrap" justifyContent="flex-end" alignSelf={{ base: "stretch", lg: "center" }}>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => navigate(`/admin/users/direct-reports/${item.user.id}?tab=absences&month=${item.entry.localStartDate.slice(0, 7)}`)}
            >
              <LuExternalLink /> {t("approvals.actions.viewContext")}
            </Button>
            <Button size="sm" variant="outline" disabled={isDeciding} onClick={() => {
              setDecisionError(null);
              setDialogTarget({ type: "absence", item });
            }}>
              <LuEye /> {t("approvals.actions.details")}
            </Button>
            <Button size="sm" colorPalette="orange" variant="outline" disabled={isDeciding} onClick={() => {
              setDecisionError(null);
              setRejectTarget({ type: "absence", item });
            }}>
              <LuX /> {t("approvals.actions.reject")}
            </Button>
            <Button
              size="sm"
              colorPalette="green"
              disabled={isDeciding}
              onClick={() => approveItem({ type: "absence", item })}
            >
              <LuCheck /> {t("approvals.actions.approve")}
            </Button>
          </Group>
        </Flex>
      </RowShell>
    );
  };

  const renderRejectedTimesheetRow = (item: TimesheetApprovalInboxItem) => (
    <RowShell key={item.entry.id} testId={`rejected-timesheet-${item.entry.id}`} accentColor="orange.500">
      <Flex direction={{ base: "column", lg: "row" }} align={{ lg: "center" }} justify="space-between" gap={3}>
        <Stack gap={1.5} flex="1">
          <Flex align="center" gap={2} wrap="wrap" color="fg" fontSize="sm">
            <Text fontWeight="semibold">{formatPicker(item.entry.localDate)}</Text>
            <Badge colorPalette={ApprovalStatusUtil.color(item.entry.status)} variant="subtle" rounded="full" px={2}>
              {ApprovalStatusUtil.label(item.entry.status)}
            </Badge>
            <Text>{item.entry.startTime}-{item.entry.endTime}</Text>
            <Text fontWeight="semibold">{formatMinutes(item.entry.minutes)} h</Text>
          </Flex>
          {item.entry.decisionReason && (
            <Alert status="warning" variant="subtle" title={t("approvals.decisionReason")} py={1.5}>
              {item.entry.decisionReason}
            </Alert>
          )}
        </Stack>
        <Group gap={2} flexWrap="wrap" justifyContent="flex-end" alignSelf={{ base: "stretch", lg: "center" }}>
          <Button size="sm" colorPalette="blue" variant="outline" disabled={isDeciding} onClick={() => {
            setDecisionError(null);
            setDialogTarget({ type: "timesheet", item });
          }}>
            <LuPencil /> {t("approvals.actions.edit")}
          </Button>
        </Group>
      </Flex>
    </RowShell>
  );

  const renderRejectedAbsenceRow = (item: AbsenceApprovalInboxItem) => (
    <RowShell key={item.entry.id} testId={`rejected-absence-${item.entry.id}`} accentColor="orange.500">
      <Flex direction={{ base: "column", lg: "row" }} align={{ lg: "center" }} justify="space-between" gap={3}>
        <Stack gap={1.5} flex="1">
          <Flex align="center" gap={2} wrap="wrap" color="fg" fontSize="sm">
            <Text fontWeight="semibold">
              {formatDateRange(item.entry.localStartDate, item.entry.localEndDate)}
            </Text>
            <Badge colorPalette={ApprovalStatusUtil.color(item.entry.status)} variant="subtle" rounded="full" px={2}>
              {ApprovalStatusUtil.label(item.entry.status)}
            </Badge>
            <Text fontWeight="semibold">{formatDay(item.entry.days)} Tg</Text>
          </Flex>
          {item.entry.decisionReason && (
            <Alert status="warning" variant="subtle" title={t("approvals.decisionReason")} py={1.5}>
              {item.entry.decisionReason}
            </Alert>
          )}
        </Stack>
        <Group gap={2} flexWrap="wrap" justifyContent="flex-end" alignSelf={{ base: "stretch", lg: "center" }}>
          <Button size="sm" colorPalette="blue" variant="outline" disabled={isDeciding} onClick={() => {
            setDecisionError(null);
            setDialogTarget({ type: "absence", item });
          }}>
            <LuPencil /> {t("approvals.actions.edit")}
          </Button>
        </Group>
      </Flex>
    </RowShell>
  );

  return (
    <Content title={t('approvals.title')} description={t('approvals.subtitle')}
      actions={<Button variant='outline' onClick={() => refresh()} loading={isLoading} disabled={isDeciding}>
        <LuRefreshCw /> {t('approvals.actions.reload')}
      </Button>}>
      <Stack gap='6' minW='0'>
        <Flex justify='start' >
          <SimpleGrid columns={{ base: 1, md: 3 }} gap='4'>
            <StatCard
              label={t("approvals.summary.pendingTimesheets")}
              value={formatMinutes(summary.pendingTimesheetMinutes)}
              unit="h"
              icon={LuClock}
              accentColor="blue.600"
              iconBg="blue.50"
              isLoading={isLoading}
            />
            <StatCard
              label={t("approvals.summary.pendingAbsences")}
              value={formatDay(summary.pendingAbsenceDays)}
              unit="Tg"
              icon={LuCalendarDays}
              accentColor="teal.600"
              iconBg="teal.50"
              isLoading={isLoading}
            />
            <StatCard
              label={t("approvals.summary.actionRequired")}
              value={summary.actionRequiredTotal}
              icon={LuTriangleAlert}
              accentColor="orange.600"
              iconBg="orange.50"
              isLoading={isLoading}
            />
          </SimpleGrid>
        </Flex>

        {decisionError && <Alert status="error" role="alert">{decisionError}</Alert>}

        {error && (
          <Alert status="error" variant="surface" title={t("approvals.loadError")}>
            {error}
          </Alert>
        )}

        {isLoading ? (
          <Stack py={10}>
            <WaitingSpinner size="xl" />
          </Stack>
        ) : (
          <Tabs.Root value={activeTab} onValueChange={({ value }) => setActiveTab(value)} lazyMount>
            <Tabs.List>
              <Tabs.Trigger value="timesheets">
                <LuClipboardCheck />
                {t("approvals.tabs.timesheets")}
                <Badge colorPalette="blue" variant="subtle" rounded="full">{inbox.pendingTimesheets.length}</Badge>
              </Tabs.Trigger>
              <Tabs.Trigger value="absences">
                <LuCalendarClock />
                {t("approvals.tabs.absences")}
                <Badge colorPalette="teal" variant="subtle" rounded="full">{inbox.pendingAbsences.length}</Badge>
              </Tabs.Trigger>
              <Tabs.Trigger value="actionRequired">
                <LuTriangleAlert />
                {t("approvals.tabs.actionRequired")}
                <Badge colorPalette="orange" variant="subtle" rounded="full">{summary.actionRequiredTotal}</Badge>
              </Tabs.Trigger>
            </Tabs.List>

            <Tabs.Content value="timesheets" px='0' pt='6' pb='0' minW='0'>
              <Stack gap='4'>
                {inbox.pendingTimesheets.length === 0
                  ? <EmptyState text={t("approvals.empty.pendingTimesheets")} />
                  : inbox.pendingTimesheets.map(renderTimesheetRow)}
              </Stack>
            </Tabs.Content>

            <Tabs.Content value="absences" px='0' pt='6' pb='0' minW='0'>
              <Stack gap='4'>
                {inbox.pendingAbsences.length === 0
                  ? <EmptyState text={t("approvals.empty.pendingAbsences")} />
                  : inbox.pendingAbsences.map(renderAbsenceRow)}
              </Stack>
            </Tabs.Content>

            <Tabs.Content value="actionRequired" px='0' pt='6' pb='0' minW='0'>
              <Stack gap='6'>
                {summary.actionRequiredTotal === 0 ? (
                  <EmptyState text={t("approvals.empty.actionRequired")} />
                ) : (
                  <>
                    {inbox.rejectedTimesheets.length > 0 && (
                      <Stack gap='4'>
                        <Flex align="center" gap={2} color="orange.fg">
                          <LuClock />
                          <Heading as='h2' size='lg'>{t("approvals.sections.rejectedTimesheets")}</Heading>
                          <Badge colorPalette="orange" variant="subtle" rounded="full">
                            {inbox.rejectedTimesheets.length}
                          </Badge>
                        </Flex>
                        {inbox.rejectedTimesheets.map(renderRejectedTimesheetRow)}
                      </Stack>
                    )}
                    {inbox.rejectedAbsences.length > 0 && (
                      <Stack gap='4'>
                        {inbox.rejectedTimesheets.length > 0 && <Separator />}
                        <Flex align="center" gap={2} color="orange.fg">
                          <LuCalendarDays />
                          <Heading as='h2' size='lg'>{t("approvals.sections.rejectedAbsences")}</Heading>
                          <Badge colorPalette="orange" variant="subtle" rounded="full">
                            {inbox.rejectedAbsences.length}
                          </Badge>
                        </Flex>
                        {inbox.rejectedAbsences.map(renderRejectedAbsenceRow)}
                      </Stack>
                    )}
                  </>
                )}
              </Stack>
            </Tabs.Content>
          </Tabs.Root>
        )}
      </Stack>

      <ApprovalRejectDialog
        heading={`${rejectHeading} ${t("approvals.actions.reject").toLowerCase()}`}
        isOpen={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        onReject={rejectItem}
      />

      {dialogTarget?.type === "timesheet" && (
        <TimesheetDialog
          key={dialogTarget.item.entry.id}
          projectNames={projectNames}
          isOpen
          onClose={() => { if (!decisionInFlight.current) setDialogTarget(null); }}
          entry={dialogTarget.item.entry}
          employeeName={employeeName(dialogTarget.item)}
          isDeciding={isDeciding}
          decisionError={decisionError}
          onSave={resubmitTimesheet}
          onDelete={cancelTimesheet}
          onApprove={() => approveItem(dialogTarget)}
          onReject={(_, reason) => decide(dialogTarget, reason)}
          canEdit={ApprovalStatusUtil.isRejected(dialogTarget.item.entry.status)}
          canCancel={ApprovalStatusUtil.isRejected(dialogTarget.item.entry.status)}
          canApprove={ApprovalStatusUtil.isPending(dialogTarget.item.entry.status)}
          canReject={ApprovalStatusUtil.isPending(dialogTarget.item.entry.status)}
        />
      )}

      {dialogTarget?.type === "absence" && (
        <AbsenceDialog
          key={dialogTarget.item.entry.id}
          isOpen
          onClose={() => { if (!decisionInFlight.current) setDialogTarget(null); }}
          entry={dialogTarget.item.entry}
          employeeName={employeeName(dialogTarget.item)}
          isDeciding={isDeciding}
          decisionError={decisionError}
          onSave={resubmitAbsence}
          onDelete={cancelAbsence}
          onApprove={() => approveItem(dialogTarget)}
          onReject={(_, reason) => decide(dialogTarget, reason)}
          canEdit={ApprovalStatusUtil.isRejected(dialogTarget.item.entry.status)}
          canCancel={ApprovalStatusUtil.isRejected(dialogTarget.item.entry.status)}
          canApprove={ApprovalStatusUtil.isPending(dialogTarget.item.entry.status)}
          canReject={ApprovalStatusUtil.isPending(dialogTarget.item.entry.status)}
        />
      )}
    </Content>
  );
};

export default OverviewApprovals;
